import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  confirmPasswordReset,
  sendPasswordResetEmail,
  verifyPasswordResetCode,
} from "firebase/auth";
import { auth } from "../../firebase";
import { getSafeAccessError } from "../api/platformAccessApi";
import andUsLogo from "../../images/logolight.png";
import "./PasswordResetPage.css";

const MIN_PASSWORD_LENGTH = 10;

function resetSettings() {
  return {
    url: `${window.location.origin}/login`,
    handleCodeInApp: false,
  };
}

export default function PasswordResetPage() {
  const [params] = useSearchParams();
  const actionCode = params.get("oobCode") || "";
  const [email, setEmail] = useState(params.get("email") || "");
  const [accountEmail, setAccountEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [status, setStatus] = useState(actionCode ? "checking" : "request");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!actionCode) return undefined;
    let active = true;
    verifyPasswordResetCode(auth, actionCode)
      .then((verifiedEmail) => {
        if (!active) return;
        setAccountEmail(verifiedEmail || "");
        setStatus("choose");
      })
      .catch((nextError) => {
        if (!active) return;
        setStatus("invalid");
        setError(getSafeAccessError(
          nextError,
          "This reset link is invalid or has expired. Request a new one.",
        ));
      });
    return () => {
      active = false;
    };
  }, [actionCode]);

  const requestReset = async (event) => {
    event.preventDefault();
    const submittedEmail = email.trim().toLowerCase();
    setError("");
    setMessage("");
    if (!submittedEmail) {
      setError("Enter the work email for your account.");
      return;
    }
    setSubmitting(true);
    try {
      await sendPasswordResetEmail(auth, submittedEmail, resetSettings());
      setMessage("Password reset instructions have been sent if an account exists for that address.");
      setStatus("sent");
    } catch (nextError) {
      if (nextError?.code === "auth/user-not-found") {
        setMessage("Password reset instructions have been sent if an account exists for that address.");
        setStatus("sent");
      } else {
        setError(getSafeAccessError(nextError, "We could not send the reset email. Please try again."));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const savePassword = async (event) => {
    event.preventDefault();
    setError("");
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Choose a password with at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (password !== confirmPassword) {
      setError("The two passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      await confirmPasswordReset(auth, actionCode, password);
      setStatus("saved");
    } catch (nextError) {
      setError(getSafeAccessError(nextError, "The new password could not be saved. Request a fresh reset link."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="password-reset">
      <section className="password-reset__panel">
        <Link className="password-reset__logo" to="/" aria-label="AndUs home">
          <img src={andUsLogo} alt="AndUs" />
        </Link>
        <p>Password reset</p>
        <h1>
          {status === "choose" ? "Choose a new password." : status === "saved" ? "Password updated." : "Reset your password."}
        </h1>
        <span>
          {status === "choose"
            ? `Set a new password${accountEmail ? ` for ${accountEmail}` : ""}.`
            : status === "saved"
              ? "You can sign in with the password you just chose."
              : "We will email a secure link if this address has an AndUs account."}
        </span>

        {error ? <p className="password-reset__alert" role="alert">{error}</p> : null}
        {message ? <p className="password-reset__alert is-success" role="status">{message}</p> : null}

        {status === "checking" ? <p className="password-reset__status">Checking your reset link...</p> : null}

        {status === "request" ? (
          <form className="password-reset__form" onSubmit={requestReset}>
            <label>
              Work email
              <input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="name@company.co.za"
                required
              />
            </label>
            <button type="submit" disabled={submitting}>
              {submitting ? "Sending..." : "Send reset link"}
            </button>
          </form>
        ) : null}

        {status === "choose" ? (
          <form className="password-reset__form" onSubmit={savePassword}>
            <label>
              New password
              <span className="password-reset__field">
                <input
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="At least 10 characters"
                  required
                />
                <button type="button" onClick={() => setShowPassword((visible) => !visible)}>
                  {showPassword ? "Hide" : "Show"}
                </button>
              </span>
            </label>
            <label>
              Confirm password
              <input
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                placeholder="Repeat the new password"
                required
              />
            </label>
            <button type="submit" disabled={submitting}>
              {submitting ? "Saving..." : "Save new password"}
            </button>
          </form>
        ) : null}

        <Link className="password-reset__back" to="/login">Back to sign in</Link>
      </section>
    </main>
  );
}
